import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, LogOut } from "lucide-react";

export function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false, growthOnly = false, requiresSubscription = false }) {
  const { user, loading, subscription, logout } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 border-4 border-sky-600 border-t-transparent rounded-full animate-spin" data-testid="loading-spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (superAdminOnly && user.role !== "super_admin") return <Navigate to="/admin" replace />;
  if (growthOnly && user.role !== "super_admin" && user.role !== "growth_manager") return <Navigate to="/admin" replace />;
  if (adminOnly && user.role !== "admin" && user.role !== "super_admin") return <Navigate to="/" replace />;

  // Subscription gate — hard lock. No access to the app until a card is
  // captured (i.e. subscription is trialing/active or the tenant is comped).
  // Super admins always bypass (ops oversight). /admin/billing is the ONE
  // place admins can reach so they can subscribe.
  if (requiresSubscription && user.role !== "super_admin") {
    // Wait until subscription state loads at least once. Avoids a flash
    // where an active tenant briefly gets redirected to /admin/billing.
    if (subscription === null) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="h-10 w-10 border-4 border-sky-600 border-t-transparent rounded-full animate-spin" data-testid="loading-subscription-spinner" />
        </div>
      );
    }
    if (!subscription.is_active) {
      if (user.role === "admin") {
        return <Navigate to="/admin/billing" state={{ from: location }} replace />;
      }
      // Employee whose tenant has no active subscription — show a friendly
      // locked screen. They can't unlock this themselves; only the admin can
      // subscribe. Give them a sign-out escape hatch so they can leave.
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="employee-subscription-locked">
          <Card className="rounded-2xl border-slate-200 w-full max-w-md">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-14 w-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                <Lock className="h-7 w-7 text-amber-700" />
              </div>
              <h2 className="text-2xl font-extrabold font-display tracking-tighter text-slate-900">Access paused</h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Your company&apos;s Production Pay Tracker subscription is not active right now. Please ask your administrator to update the plan — once they do, you&apos;ll have immediate access.
              </p>
              <Button onClick={logout} variant="outline" className="w-full" data-testid="employee-locked-signout-btn">
                <LogOut className="h-4 w-4 mr-1.5" /> Sign out
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  return children;
}
